import { useEffect, useMemo, useState } from "react";
import {
  GENRES,
  numJP,
  priceJP,
  type Author,
  type Work,
} from "../data";
import type { Nav, ToastInfo } from "../types";
import {
  AgeBadge,
  Footer,
  GenreBadge,
  LoadingBlock,
  Placeholder,
  Stars,
  WorkCard,
  WorkThumb,
} from "../components/atoms";
import { useFetch } from "../hooks";
import {
  addToLibrary,
  fetchAuthor,
  fetchAuthorWorks,
  fetchMyWorkStatus,
  fetchReviews,
  fetchSimilarWorks,
  fetchWork,
  postReview,
  removeFromLibrary,
  workImageUrl,
} from "../api";
import { useT } from "../i18n";
import { useAuth } from "../auth";

const ENGINE_URL =
  (import.meta.env.VITE_ENGINE_URL as string | undefined) ?? "https://play.pixadv.net";

interface Props {
  workId: string;
  nav: Nav;
  onToast?: (t: ToastInfo) => void;
}

type ReviewSort = "rating" | "new" | "helpful";

export function WorkPage({ workId, nav, onToast }: Props) {
  const t = useT();
  const { user } = useAuth();
  const workFetch = useFetch(() => fetchWork(workId), [workId]);
  const work = workFetch.data;
  const authorFetch = useFetch(
    async () => (work ? fetchAuthor(work.authorId) : null),
    [work?.authorId],
  );
  const reviewsFetch = useFetch(
    async () => (work ? fetchReviews(work.id) : null),
    [work?.id],
  );
  const similarFetch = useFetch(
    async () => (work ? fetchSimilarWorks(work.id) : []),
    [work?.id],
  );
  const sameAuthorFetch = useFetch(
    async () =>
      work
        ? (await fetchAuthorWorks(work.authorId)).filter((w) => w.id !== work.id)
        : [],
    [work?.authorId, work?.id],
  );
  const statusFetch = useFetch(
    async () => (work && user ? fetchMyWorkStatus(work.id) : null),
    [work?.id, user?.id],
  );

  const [reviewSort, setReviewSort] = useState<ReviewSort>("rating");
  const [inLibrary, setInLibrary] = useState(false);
  const [showR18, setShowR18] = useState(work?.ageRating !== "r18");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState<number>(5);
  const [reviewBody, setReviewBody] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);

  useEffect(() => {
    if (statusFetch.data) {
      setInLibrary(statusFetch.data.inLibrary);
      if (statusFetch.data.myReview) {
        setReviewRating(statusFetch.data.myReview.rating);
        setReviewBody(statusFetch.data.myReview.body);
      }
    }
  }, [statusFetch.data]);

  const sortedReviews = useMemo(() => {
    const arr = reviewsFetch.data?.reviews ? [...reviewsFetch.data.reviews] : [];
    if (reviewSort === "rating") arr.sort((a, b) => b.rating - a.rating);
    if (reviewSort === "new") arr.sort((a, b) => b.createdAt - a.createdAt);
    return arr;
  }, [reviewsFetch.data, reviewSort]);

  const dist = useMemo(() => {
    const reviews = reviewsFetch.data?.reviews ?? [];
    return [5, 4, 3, 2, 1].map((r) => ({
      r,
      count: reviews.filter((rv) => rv.rating === r).length,
    }));
  }, [reviewsFetch.data]);
  const maxDist = Math.max(...dist.map((d) => d.count), 1);

  if (workFetch.loading || !work) {
    return (
      <div className="page page-work">
        <div className="container" style={{ paddingTop: 40 }}>
          <LoadingBlock height={420} />
        </div>
      </div>
    );
  }
  if (workFetch.error) {
    return (
      <div className="page page-work">
        <div className="container" style={{ paddingTop: 40 }}>
          <div className="empty">
            <div className="empty-title">{t("common.error")}</div>
            <div className="empty-desc">{workFetch.error}</div>
          </div>
        </div>
      </div>
    );
  }

  const author: Author | null = authorFetch.data;
  const reviews = reviewsFetch.data?.reviews ?? [];
  const reviewTones = reviewsFetch.data?.tones ?? [];

  function play() {
    if (!work) return;
    onToast?.({ kind: "info", text: t("play.openingEngine") });
    window.open(`${ENGINE_URL}?work=${encodeURIComponent(work.id)}`, "_blank");
  }
  async function buy() {
    if (!user) {
      nav({ page: "auth", mode: "login" });
      return;
    }
    try {
      await addToLibrary(work!.id);
      onToast?.({
        kind: "success",
        text: t("work.purchaseDone", {
          title: work!.title,
          price: priceJP(work!.price),
        }),
      });
      setInLibrary(true);
    } catch (e) {
      onToast?.({
        kind: "warn",
        text: e instanceof Error ? e.message : String(e),
      });
    }
  }
  async function favorite() {
    if (!user) {
      nav({ page: "auth", mode: "login" });
      return;
    }
    try {
      if (inLibrary) {
        await removeFromLibrary(work!.id);
        setInLibrary(false);
        onToast?.({ kind: "success", text: t("work.removeLibraryDone") });
      } else {
        await addToLibrary(work!.id);
        setInLibrary(true);
        onToast?.({ kind: "success", text: t("work.addLibraryDone") });
      }
    } catch (e) {
      onToast?.({
        kind: "warn",
        text: e instanceof Error ? e.message : String(e),
      });
    }
  }
  async function submitReview() {
    if (!work) return;
    if (!reviewBody.trim()) return;
    setReviewSaving(true);
    try {
      await postReview(work.id, reviewRating, reviewBody.trim());
      onToast?.({ kind: "success", text: t("review.posted") });
      setReviewOpen(false);
      statusFetch.reload();
      reviewsFetch.reload();
    } catch (e) {
      onToast?.({
        kind: "warn",
        text: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setReviewSaving(false);
    }
  }

  return (
    <div className="page page-work">
      <div className="container">
        <div
          style={{
            fontSize: 12,
            color: "var(--muted-2)",
            fontFamily: "var(--font-mono)",
            marginTop: 16,
          }}
        >
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              nav({ page: "home" });
            }}
            style={{ color: "var(--muted-2)" }}
          >
            {t("work.home")}
          </a>
          {" / "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              nav({ page: "search", genre: work.genre });
            }}
            style={{ color: "var(--muted-2)" }}
          >
            {GENRES[work.genre]?.ja}
          </a>
          {" / "}
          <span style={{ color: "var(--accent)" }}>{work.title}</span>
        </div>

        <div className="work-layout">
          <main>
            <div className="work-hero">
              <div className="wh-thumb">
                {work.ageRating === "r18" && !showR18 ? (
                  <div
                    className="ph ph-tone-other"
                    style={{
                      aspectRatio: "16/9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        color: "var(--warn)",
                        fontFamily: "var(--font-mono)",
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                    >
                      {t("work.r18Title")}
                    </div>
                    <div style={{ color: "var(--muted-2)", fontSize: 12 }}>
                      {t("work.r18Note")}
                    </div>
                    <button className="btn btn-sm" onClick={() => setShowR18(true)}>
                      {t("work.r18Show")}
                    </button>
                  </div>
                ) : (
                  <WorkThumb
                    work={work}
                    caption={`KEY-VISUAL // ${work.title}`}
                    seed={work.id.charCodeAt(2)}
                  />
                )}
                <div
                  className="wbadges"
                  style={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    display: "flex",
                    gap: 6,
                  }}
                >
                  <GenreBadge genre={work.genre} />
                  <AgeBadge rating={work.ageRating} />
                </div>
              </div>
              <div className="wh-body">
                <h1>{work.title}</h1>
                <div className="wh-author">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      nav({ page: "author", id: work.authorId });
                    }}
                  >
                    {author?.displayName ?? work.authorName ?? ""}
                  </a>
                  {author?.handle && (
                    <span style={{ color: "var(--muted-2)" }}> @{author.handle}</span>
                  )}
                </div>
                <div className="wh-meta-row">
                  <div className="wh-rating">
                    <Stars rating={work.rating ?? 0} size={16} />
                    <span className="score">{(work.rating ?? 0).toFixed(1)}</span>
                    <span className="count">/ {numJP(work.reviews ?? 0)}</span>
                  </div>
                  <div className="spacer" style={{ flex: 1 }}></div>
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 24,
                      color: "var(--accent)",
                      fontWeight: 700,
                    }}
                  >
                    {priceJP(work.price)}
                  </div>
                </div>
                <div className="wh-actions">
                  {!work.price || work.price === 0 ? (
                    <button className="btn btn-primary btn-lg" onClick={play}>
                      {t("home.playFree")}
                    </button>
                  ) : inLibrary ? (
                    <button className="btn btn-primary btn-lg" onClick={play}>
                      {t("work.play")}
                    </button>
                  ) : (
                    <button className="btn btn-accent btn-lg" onClick={buy}>
                      {t("work.buy")} · {priceJP(work.price)}
                    </button>
                  )}
                  <button className="btn" onClick={favorite}>
                    {inLibrary ? t("work.inLibrary") : t("work.notInLibrary")}
                  </button>
                  <button className="btn btn-ghost btn-icon" title={t("work.share")}>
                    <span style={{ fontFamily: "var(--font-mono)" }}>𝕏</span>
                  </button>
                  <button
                    className="btn btn-ghost btn-icon"
                    title={t("work.copyLink")}
                    onClick={() =>
                      onToast?.({ kind: "success", text: t("work.copyLinkDone") })
                    }
                  >
                    <span style={{ fontFamily: "var(--font-mono)" }}>⎘</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Screenshots */}
            <h3
              style={{
                marginTop: 28,
                marginBottom: 8,
                fontSize: 13,
                fontFamily: "var(--font-mono)",
                color: "var(--muted-2)",
                textTransform: "uppercase",
                letterSpacing: "0.15em",
              }}
            >
              {t("work.screenshots", {
                count: work.screenshots?.length || 6,
              })}
            </h3>
            <div className="gallery">
              {work.screenshots && work.screenshots.length > 0
                ? work.screenshots.map((s, i) => {
                    const url = workImageUrl(work.id, s);
                    return (
                      <div
                        key={s + i}
                        className={`ph ph-tone-${GENRES[work.genre]?.tone ?? "other"} ph-image`}
                        style={{ aspectRatio: "16/9" }}
                      >
                        {url && <img src={url} alt={`screenshot ${i + 1}`} loading="lazy" />}
                      </div>
                    );
                  })
                : Array.from({ length: 6 }).map((_, i) => (
                    <Placeholder
                      key={i}
                      caption={`scene_${String(i + 1).padStart(2, "0")}.png`}
                      tone={GENRES[work.genre]?.tone}
                      seed={i + 21}
                      aspect="16/9"
                    />
                  ))}
            </div>

            {/* Description */}
            <div
              className="desc-md"
              style={{ marginTop: 24 }}
              dangerouslySetInnerHTML={{
                __html: renderSimpleMarkdown(work.description || defaultDescription(work)),
              }}
            ></div>

            {/* Reviews */}
            <section className="section">
              <div className="section-head">
                <h2>
                  {t("work.reviews")}{" "}
                  <span className="sh-en">
                    {t("work.reviewsEn", { count: numJP(work.reviews ?? 0) })}
                  </span>
                </h2>
              </div>

              <div className="review-summary">
                <div className="review-score-big">
                  <div className="num">{(work.rating ?? 0).toFixed(1)}</div>
                  <Stars rating={work.rating ?? 0} size={14} />
                  <div className="count">
                    {t("work.reviewsCount", { count: numJP(work.reviews ?? 0) })}
                  </div>
                </div>
                <div>
                  <div className="review-dist">
                    {dist.map((d) => (
                      <div className="review-dist-row" key={d.r}>
                        <span className="rd-label">★ {d.r}</span>
                        <div className="rd-bar">
                          <div
                            className="rd-fill"
                            style={{ width: `${(d.count / maxDist) * 100}%` }}
                          ></div>
                        </div>
                        <span className="rd-count">{d.count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="review-tones">
                    {reviewTones.map((tone) => (
                      <span key={tone.name} className="review-tone">
                        {tone.name} <span className="rt-count">×{tone.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* レビュー投稿フォーム */}
              {user ? (
                <div className="review-form">
                  {!reviewOpen ? (
                    <button
                      className="btn btn-accent btn-sm"
                      onClick={() => setReviewOpen(true)}
                    >
                      {statusFetch.data?.myReview
                        ? t("review.editBtn") + " · " + t("review.myMark")
                        : t("review.writeTitle")}
                    </button>
                  ) : (
                    <div className="review-form-body">
                      <div className="rf-row">
                        <label>{t("review.rating")}</label>
                        <div className="rf-stars">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              className={
                                "rf-star" + (reviewRating >= n ? " on" : "")
                              }
                              onClick={() => setReviewRating(n)}
                              aria-label={`${n} stars`}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="rf-row">
                        <label>{t("review.body")}</label>
                        <textarea
                          value={reviewBody}
                          onChange={(e) => setReviewBody(e.target.value)}
                          placeholder={t("review.placeholder")}
                          rows={4}
                        />
                      </div>
                      <div className="rf-actions">
                        <button
                          className="btn btn-sm"
                          onClick={() => setReviewOpen(false)}
                          disabled={reviewSaving}
                        >
                          {t("review.cancel")}
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => void submitReview()}
                          disabled={reviewSaving || !reviewBody.trim()}
                        >
                          {statusFetch.data?.myReview
                            ? t("review.update")
                            : t("review.submit")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--muted-2)",
                    marginBottom: 12,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {t("review.requireLogin")}{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      nav({ page: "auth", mode: "login" });
                    }}
                  >
                    {t("review.loginNow")}
                  </a>
                </div>
              )}

              <div className="review-sort">
                <button
                  className={`rsort ${reviewSort === "rating" ? "active" : ""}`}
                  onClick={() => setReviewSort("rating")}
                >
                  {t("work.sortRating")}
                </button>
                <button
                  className={`rsort ${reviewSort === "new" ? "active" : ""}`}
                  onClick={() => setReviewSort("new")}
                >
                  {t("work.sortNew")}
                </button>
                <button
                  className={`rsort ${reviewSort === "helpful" ? "active" : ""}`}
                  onClick={() => setReviewSort("helpful")}
                >
                  {t("work.sortHelpful")}
                </button>
              </div>

              <div className="review-list">
                {reviewsFetch.loading && reviews.length === 0 ? (
                  <LoadingBlock height={120} />
                ) : (
                  sortedReviews.map((r) => (
                    <div className="review-card" key={r.id}>
                      <div className="rc-head">
                        <span className="rc-user">{r.userName}</span>
                        <Stars rating={r.rating} size={12} />
                        {r.hasPurchased && (
                          <span className="rc-pmark">{t("work.purchasedMark")}</span>
                        )}
                        <span className="rc-date">
                          {new Date(r.createdAt).toISOString().slice(0, 10)}
                        </span>
                      </div>
                      <div className="rc-body">{r.body}</div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* Same author */}
            {(sameAuthorFetch.data?.length ?? 0) > 0 && (
              <section className="section">
                <div className="section-head">
                  <h2>
                    {t("work.sameAuthor", {
                      name: author?.displayName ?? work.authorName ?? "",
                    })}{" "}
                    <span className="sh-en">{t("work.sameAuthorEn")}</span>
                  </h2>
                  <a
                    href="#"
                    className="sh-more"
                    onClick={(e) => {
                      e.preventDefault();
                      nav({ page: "author", id: work.authorId });
                    }}
                  >
                    {t("home.viewAll")}
                  </a>
                </div>
                <div className="grid-4">
                  {sameAuthorFetch.data!.slice(0, 4).map((w, i) => (
                    <WorkCard
                      key={w.id}
                      work={w}
                      onClick={(x) => nav({ page: "work", id: x.id })}
                      seed={i + 30}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Similar */}
            {(similarFetch.data?.length ?? 0) > 0 && (
              <section className="section">
                <div className="section-head">
                  <h2>
                    {t("work.similar")} <span className="sh-en">{t("work.similarEn")}</span>
                  </h2>
                </div>
                <div className="grid-4">
                  {similarFetch.data!.map((w, i) => (
                    <WorkCard
                      key={w.id}
                      work={w}
                      onClick={(x) => nav({ page: "work", id: x.id })}
                      seed={i + 40}
                    />
                  ))}
                </div>
              </section>
            )}
          </main>

          <aside>
            <div className="sidebar-panel">
              <h3>{t("work.info")}</h3>
              <div className="info-list">
                <InfoRow label={t("work.infoId")} valueMono value={work.id} />
                <InfoRow label={t("work.infoGenre")} value={GENRES[work.genre]?.ja ?? "-"} />
                <InfoRow
                  label={t("work.infoPlayTime")}
                  value={t("work.infoPlayTimeFmt", {
                    min: String(work.estimatedPlayTime ?? "?"),
                  })}
                />
                <InfoRow
                  label={t("work.infoPrice")}
                  value={priceJP(work.price)}
                  valueStyle={{ color: work.price ? "var(--accent)" : "var(--success)" }}
                />
                <InfoRow
                  label={t("work.infoAge")}
                  value={
                    work.ageRating === "all"
                      ? t("common.ageAll")
                      : work.ageRating === "r15"
                        ? t("common.ageR15")
                        : t("common.ageR18")
                  }
                />
                <InfoRow
                  label={t("work.infoUpdated")}
                  value={work.updatedAt}
                  valueMono
                  valueStyle={{ color: "var(--sub)" }}
                />
                <InfoRow
                  label={t("work.infoPublished")}
                  value={work.publishedAt ?? "-"}
                  valueMono
                  valueStyle={{ color: "var(--sub)" }}
                />
                <InfoRow
                  label={t("work.infoPlays")}
                  value={numJP(work.stats.plays)}
                  valueMono
                  valueStyle={{ color: "var(--sub)" }}
                />
                <InfoRow
                  label={t("work.infoFavorites")}
                  value={numJP(work.stats.favorites)}
                  valueMono
                  valueStyle={{ color: "var(--sub)" }}
                />
              </div>
            </div>

            <div className="sidebar-panel">
              <h3>{t("work.tags")}</h3>
              <div className="tag-list">
                {work.tags.map((tg) => (
                  <span
                    className="tag"
                    key={tg}
                    onClick={() => nav({ page: "search", tag: tg })}
                  >
                    #{tg}
                  </span>
                ))}
              </div>
            </div>

            <div className="sidebar-panel">
              <h3>{t("work.author")}</h3>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div
                  className="avimg"
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 4,
                    background:
                      "linear-gradient(135deg, var(--accent), var(--primary))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "var(--font-mono)",
                    fontWeight: 800,
                    fontSize: 20,
                    color: "var(--bg)",
                  }}
                >
                  {author?.initials ?? work.authorInitials ?? "?"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "var(--accent)", fontWeight: 700 }}>
                    {author?.displayName ?? work.authorName ?? ""}
                  </div>
                  {author?.handle && (
                    <div
                      style={{
                        color: "var(--muted-2)",
                        fontSize: 11,
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      @{author.handle} ·{" "}
                      {t("work.authorWorks", { count: author.workCount ?? 0 })}
                    </div>
                  )}
                </div>
              </div>
              <div
                style={{
                  color: "var(--sub)",
                  fontSize: 12,
                  marginTop: 10,
                  lineHeight: 1.6,
                }}
              >
                {author?.bio ?? ""}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                <button
                  className="btn btn-sm btn-block"
                  onClick={() => nav({ page: "author", id: work.authorId })}
                >
                  {t("work.profileBtn")}
                </button>
                <button className="btn btn-sm" style={{ flexShrink: 0 }}>
                  {t("work.followBtn")}
                </button>
              </div>
            </div>

            <div
              className="sidebar-panel"
              style={{ background: "transparent", border: "1px dashed var(--border)" }}
            >
              <h3 style={{ color: "var(--muted-2)" }}>{t("work.notice")}</h3>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--muted-2)",
                  lineHeight: 1.7,
                  fontFamily: "var(--font-mono)",
                  whiteSpace: "pre-line",
                }}
              >
                {t("work.noticeBody")}
                <br />
                {t("work.noticeRefund")}{" "}
                <a href="#" style={{ color: "var(--primary)" }}>
                  {t("work.noticeRefundHere")}
                </a>
                。
              </div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function InfoRow({
  label,
  value,
  valueMono,
  valueStyle,
}: {
  label: string;
  value: string;
  valueMono?: boolean;
  valueStyle?: React.CSSProperties;
}) {
  return (
    <div className="info-row">
      <span className="ilabel">{label}</span>
      <span className={"ival" + (valueMono ? " mono" : "")} style={valueStyle}>
        {value}
      </span>
    </div>
  );
}

function defaultDescription(work: Work): string {
  return `## あらすじ\n\n**${work.title}** — ${work.shortDescription ?? ""}\n\n*ジャンル:* ${GENRES[work.genre]?.ja}　*プレイ時間:* 約${work.estimatedPlayTime}分\n\n### この作品について\n\n本作はピックアップ作品の一つです。物語は静かに始まり、思いがけない結末へと向かいます。\n\n- ボイスなし、BGMあり\n- セーブ可能、いつでも再開できます\n- ${work.ageRating === "all" ? "全年齢対象です" : work.ageRating === "r15" ? "R-15: 軽度の性描写・暴力描写を含みます" : "R-18: 成人向け描写を含みます"}\n\n> 続きを読みたければ、今夜、画面の向こうで会いましょう。`;
}

function renderSimpleMarkdown(md: string): string {
  let h = md.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  h = h.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  h = h.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  h = h.replace(/^> (.+)$/gm, "<blockquote>$1</blockquote>");
  h = h.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  h = h.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  h = h.replace(/`([^`]+)`/g, "<code>$1</code>");
  h = h.replace(/(^|\n)((?:- .+\n?)+)/g, (_m, pre: string, blk: string) => {
    const items = blk
      .trim()
      .split("\n")
      .map((l) => l.replace(/^- /, ""))
      .map((l) => `<li>${l}</li>`)
      .join("");
    return `${pre}<ul>${items}</ul>`;
  });
  h = h
    .split(/\n{2,}/)
    .map((p) => {
      if (p.match(/^<(h2|h3|ul|blockquote)/)) return p;
      if (!p.trim()) return "";
      return `<p>${p.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");
  return h;
}
